import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TextInput } from '@els/els-react--text-input';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import cn from 'classnames';
import uuid from 'uuid/v4';
import { KEYCODES } from '@Utils/KeyboardUtil';
import { useValidateSmiles } from './validateSmilesHook';
import SimpleSpinner from '../../../../Components/Results/utils/SimpleSpinner';
import Substructure from '../Substructure/Substructure';
import { IK_PARAMS_SMILES_COUNT_ERROR, IK_PARAMS_SMILES_INVALID_ERROR, IK_PARAMS_SMILES_MAX_COUNT } from '../constants';
import RsSlidable, { CONTROLS_PLACEMENTS } from '../../components/RsSlidable/RsSlidable';

import './SmilesSection.theme.scss';

/* eslint-disable react-hooks/exhaustive-deps */
function SmilesSection({ section, smiles, setSmiles }) {
  const spinnerId = 'smilesSectionSpinner';
  const [smilesString, setSmilesString] = useState('');
  const [smilesError, setSmilesError] = useState('');
  const { validSmiles, error, isLoading, validate } = useValidateSmiles();
  const [visibleIndex, setVisibleIndex] = useState(0);

  const addSmiles = () => {
    if (isEmpty(smilesString.trim())) {
      setSmilesError(IK_PARAMS_SMILES_INVALID_ERROR);
      return;
    }
    const newSmilesArray = [
      ...new Set(
        smilesString
          .split(',')
          .filter((v) => !isEmpty(v.trim()))
          .map((v) => ({ value: v.trim(), id: uuid() }))
      ),
    ];

    if (smiles.length + newSmilesArray.length > IK_PARAMS_SMILES_MAX_COUNT) {
      setSmilesError(IK_PARAMS_SMILES_COUNT_ERROR);
    } else {
      validate(newSmilesArray);
    }
  };

  const removeSmiles = (v) => {
    const index = smiles.findIndex((s) => s.id === v.id);
    setVisibleIndex(index > 0 ? index - 1 : 0);
    setSmiles(smiles.filter((s) => s.id !== v.id));
  };

  useEffect(() => {
    if (error) {
      setSmilesError(IK_PARAMS_SMILES_INVALID_ERROR);
    } else if (validSmiles) {
      setSmilesError('');
      setSmilesString('');
      setVisibleIndex(smiles.length + validSmiles.length - 1);
      setSmiles([...smiles, ...validSmiles]);
    }
  }, [validSmiles, error]);

  const itemsRenderer = (item) => {
    return <Substructure key={item} smiles={item} removeSmiles={removeSmiles} />;
  };

  const contentAttrs = {
    'aria-label': 'smiles',
  };
  const itemAttrs = ({ id, context }) => {
    return {
      id: `smiles-list-${context}-${id}`,
      'aria-label': 'smile',
    };
  };

  const handleInputKeyDown = (e) => {
    if (e.keyCode === KEYCODES.ENTER) {
      addSmiles();
    }
  };

  const handleInputChange = (e) => {
    setSmilesString(e.target.value);
  };

  return (
    <div className="smiles-section">
      {isLoading && <SimpleSpinner id={spinnerId} />}
      <div className="mgt">
        {capitalize(section)} substructures (up to 10)
        <TextInput
          size="small"
          iconRight={{
            id: `addSmilesIncludes-${section}`,
            sprite: 'AddCircle',
            size: 'xs',
            a11y: { name: 'Add substurcture' },
          }}
          iconRightCallback={addSmiles}
          label="Enter SMILES"
          shouldHideLabel
          className="smiles-section__smiles-input"
          value={smilesString}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
        />
        <span className="smiles-section__smiles-input__error">{smilesError}</span>
      </div>
      <div className="mgt smiles-section__smiles-list">
        <RsSlidable
          className="smiles-section__smiles-list__slidable flex-1"
          containerClassName="smiles-section__smiles-list__list-container"
          contentClassName="smiles-section__smiles-list__list-item__content"
          contentAttrs={contentAttrs}
          itemClassName={(item, i) =>
            cn('smiles-section__smiles-list__list-item', {
              last: i === smiles.length - 1,
            })
          }
          itemAttrs={itemAttrs}
          items={smiles}
          renderItem={itemsRenderer}
          controlsPlacement={CONTROLS_PLACEMENTS.rightSide}
          resized={smiles.length}
          visibleItemIndex={visibleIndex}
        />
      </div>
    </div>
  );
}

SmilesSection.defaultProps = {};

SmilesSection.propTypes = {
  section: PropTypes.string.isRequired,
  smiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSmiles: PropTypes.func.isRequired,
};

export default SmilesSection;
